import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Folder } from 'lucide-react';
import { Button } from './ui/button';

const Articles = ({ articles }) => {
  if (!articles || articles.length === 0) return null;

  return (
    <section className="py-20 pb-28 bg-[#f5f5f5] overflow-hidden" data-testid="articles-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-end mb-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true, margin: "-80px" }}
          >
            <span className="text-sm font-semibold text-[#F6BF58] uppercase tracking-wider">
              articles
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2">
              Your Source to Stay Informed
            </h2>
            <motion.div
              className="w-16 h-1 bg-[#9E1B32] mt-4"
              initial={{ scaleX: 0, originX: 0 }}
              whileInView={{ scaleX: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true, margin: "-80px" }}
            />
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true, margin: "-80px" }}
            className="lg:text-right"
          >
            <p className="text-slate-600 mb-4">
              Citizens all over the state are working hard in many different ways to secure our elections. This is your resource to stay up-to-date on our efforts to secure our elections.
            </p>
            <Button
              asChild
              variant="outline"
              className="border-[#9E1B32] text-[#9E1B32] hover:bg-[#9E1B32] hover:text-white"
              data-testid="view-more-articles"
            >
              <Link to="/news">
                View more
                <ArrowRight size={16} className="ml-2" />
              </Link>
            </Button>
          </motion.div>
        </div>

        {/* Articles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {articles.slice(0, 3).map((article, index) => (
            <motion.article
              key={article.id || index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.15, ease: [0.22, 1, 0.36, 1] }}
              viewport={{ once: true, margin: "-60px" }}
              className="bg-white rounded-lg overflow-hidden shadow-md group"
              data-testid={`article-card-${index}`}
            >
              <Link to={`/blog/${article.url || article.slug || ''}`}>
                {/* Image */}
                <div className="relative overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {/* Date badge */}
                  <div className="absolute bottom-0 left-0 bg-[#9E1B32] text-white px-4 py-2 clip-path-date">
                    <span className="block text-xl font-bold">
                      {new Date(article.date).toLocaleDateString('en-US', { day: '2-digit' })}
                    </span>
                    <span className="text-sm">
                      {new Date(article.date).toLocaleDateString('en-US', { month: 'short' })}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-3">
                    <Folder size={14} />
                    <span>{article.category}</span>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 group-hover:text-[#9E1B32] transition-colors mb-3 line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-slate-600 text-sm line-clamp-2 mb-4">
                    {article.excerpt}
                  </p>
                  <span className="inline-flex items-center gap-2 text-[#9E1B32] font-semibold text-sm group-hover:gap-3 transition-all duration-300">
                    READ MORE
                    <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Articles;
